import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CaptionStatus, DesignRenderGroup } from "@/services/bedrock/dtos/DesignRenderGroup";
import { DesignRenderGroupServices } from "@/services/bedrock/DesignRenderGroupServices";
import { designKeys } from "@/services/bedrock/queryKeys";
import { isApiError } from "@/services/bedrock/apiError";
import { CableStatus, useCableSubscription } from "@/utils/actioncable";

export const DESIGN_RENDER_GROUP_CHANNEL = "DesignRenderGroupChannel";
const POLL_INTERVAL_MS = 5_000;

export interface UseRenderGroupResult {
  group: DesignRenderGroup | undefined;
  isInFlight: boolean;
  isFailed: boolean;
  notFound: boolean;
  error: unknown;
  connection: CableStatus;
  caption: string | null;
  captionStatus: CaptionStatus;
}

function groupInFlight(group: DesignRenderGroup | undefined): boolean {
  return group?.status === "pending" || group?.status === "processing";
}

function captionInFlight(group: DesignRenderGroup | undefined): boolean {
  return group?.captionStatus === "pending";
}

/**
 * React Query holds the render group; the channel writes each full payload into it and polling
 * only runs while the socket is down and something is still in flight.
 */
export function useRenderGroup(id: number | null): UseRenderGroupResult {
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (payload: DesignRenderGroup) => {
      if (id == null || !payload || typeof payload !== "object") return;
      queryClient.setQueryData(designKeys.renderGroup(id), payload);
    },
    [id, queryClient]
  );

  // A broadcast sent before the subscription was confirmed never arrives, and polling stops once
  // connected, so refetch on every (re)connect.
  const resync = useCallback(() => {
    if (id != null) queryClient.invalidateQueries({ queryKey: designKeys.renderGroup(id) });
  }, [id, queryClient]);

  const { status: connection } = useCableSubscription<DesignRenderGroup>({
    channel: DESIGN_RENDER_GROUP_CHANNEL,
    params: { id },
    enabled: id != null,
    onMessage: handleMessage,
    onConnected: resync,
    onReconnect: resync,
  });

  const query = useQuery({
    queryKey: designKeys.renderGroup(id ?? -1),
    queryFn: () => DesignRenderGroupServices.get(id as number),
    enabled: id != null,
    retry: false,
    refetchInterval: q => {
      const group = q.state.data as DesignRenderGroup | undefined;
      const stillWorking = groupInFlight(group) || captionInFlight(group);
      return connection !== "connected" && stillWorking ? POLL_INTERVAL_MS : false;
    },
  });

  const group = query.data;
  const error = query.error;
  const notFound = isApiError(error, "not_found") || isApiError(error, "forbidden");

  return {
    group,
    isInFlight: groupInFlight(group),
    isFailed: group?.status === "failed",
    notFound,
    error,
    connection,
    caption: group?.caption ?? null,
    captionStatus: group?.captionStatus ?? "none",
  };
}
